import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, FileCode2, Search, ExternalLink, Home, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

const PLANNING_STATUS_STYLE = {
  Permitted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Conditions: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Rejected: "bg-rose-100 text-rose-800 border-rose-200",
  Withdrawn: "bg-rose-100 text-rose-800 border-rose-200",
  Undecided: "bg-amber-100 text-amber-800 border-amber-200",
  Referred: "bg-amber-100 text-amber-800 border-amber-200",
  Unresolved: "bg-amber-100 text-amber-800 border-amber-200",
  Unknown: "bg-muted text-muted-foreground border-border",
};

function formatDMY(isoDate) {
  if (!isoDate) return "";
  const parts = String(isoDate).slice(0, 10).split("-");
  if (parts.length !== 3) return isoDate;
  const [y, m, d] = parts;
  return `${d}/${m}/${y}`;
}

function AppRow({ a }) {
  return (
    <a
      href={a.link || undefined}
      target="_blank"
      rel="noreferrer"
      className="block border rounded-md px-3 py-2 text-sm hover:bg-muted/30 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="truncate min-w-0 flex-1 font-medium">{a.address}</span>
        <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${PLANNING_STATUS_STYLE[a.status] || PLANNING_STATUS_STYLE.Unknown}`}>
          {a.status}
        </span>
      </div>
      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{a.description}</p>
      <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-1">
        {formatDMY(a.date)}
        {a.link && <ExternalLink className="h-3 w-3 ml-1" />}
      </div>
    </a>
  );
}

/**
 * Agent dashboard -> "Planning Applications" section. Two parts:
 * 1) Auto-loaded planning activity near each of the agent's own listings.
 * 2) A manual postcode search for anywhere else (e.g. a prospective
 *    seller's address that isn't one of the agent's listings yet).
 */
export default function PlanningApplicationsTool({ linkedProperties = [], publicMode = false }) {
  const [myResults, setMyResults] = useState([]);
  const [loadingMine, setLoadingMine] = useState(true);

  const [postcode, setPostcode] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [searchError, setSearchError] = useState(null);

  const myProperties = useMemo(
    () => linkedProperties.filter((p) => p.property_id && p.postcode),
    [linkedProperties]
  );

  useEffect(() => {
    if (myProperties.length === 0) {
      setLoadingMine(false);
      return;
    }
    setLoadingMine(true);
    Promise.all(
      myProperties.map((p) =>
        axios
          .get(`${API}/property-admin/property/${p.property_id}/planning-applications`, { withCredentials: true })
          .then(({ data }) => ({
            property_id: p.property_id,
            address: p.display_address || p.postcode,
            postcode: data.postcode,
            applications: data.applications || [],
          }))
          .catch(() => ({ property_id: p.property_id, address: p.display_address || p.postcode, postcode: p.postcode, applications: [] }))
      )
    )
      .then(setMyResults)
      .finally(() => setLoadingMine(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myProperties.map((p) => p.property_id).join(",")]);

  const handleSearch = async (e) => {
    e.preventDefault();
    const pc = postcode.trim();
    if (!pc) return;
    setSearching(true);
    setSearchError(null);
    try {
      const { data } = await axios.post(
        `${API}/${publicMode ? "public" : "property-admin"}/planning-applications/search`,
        { postcode: pc },
        { withCredentials: !publicMode }
      );
      setSearchResult(data);
    } catch (e2) {
      const msg = e2?.response?.data?.detail || e2?.message || "Failed to search planning applications";
      setSearchError(msg);
      toast.error(msg);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="planning-applications-tool">
      <div className="bg-white border border-border p-6">
        <h3 className="font-serif text-xl font-medium flex items-center gap-2">
          <FileCode2 className="h-5 w-5 text-emerald-600" />
          Planning Applications
        </h3>
        <p className="text-sm text-muted-foreground mt-1 mb-4">
          {publicMode
            ? "Real planning activity from PlanIt (planit.org.uk) — search any UK postcode."
            : "Real planning activity from PlanIt (planit.org.uk) — automatically shown for your own listings below, or search any other postcode."}
        </p>

        <form onSubmit={handleSearch} className="flex items-center gap-2 max-w-md">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              placeholder="e.g. EN11 0RD"
              className="pl-9 rounded-none"
              data-testid="planning-apps-tool-postcode-input"
            />
          </div>
          <Button type="submit" disabled={searching || !postcode.trim()} className="rounded-none" data-testid="planning-apps-tool-search-btn">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
          </Button>
        </form>
      </div>

      {searching && (
        <div className="flex justify-center py-6" data-testid="planning-apps-tool-search-loading">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}
      {!searching && searchError && (
        <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md p-4" data-testid="planning-apps-tool-search-error">
          {searchError}
        </div>
      )}
      {!searching && searchResult && (
        <div className="bg-white border border-border p-6 space-y-2" data-testid="planning-apps-tool-search-result">
          <p className="text-xs font-semibold text-muted-foreground uppercase">
            Results for {searchResult.postcode} ({searchResult.applications.length})
          </p>
          {searchResult.cached && (
            <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="planning-apps-tool-cached-indicator">
              <Clock className="h-3 w-3" /> Cached result from {formatDMY(searchResult.cached_at)}
            </p>
          )}
          {searchResult.applications.length > 0 ? (
            <div className="space-y-1.5">
              {searchResult.applications.map((a, i) => <AppRow key={i} a={a} />)}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">No planning applications found for this postcode.</p>
          )}
        </div>
      )}

      {!publicMode && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2 flex items-center gap-1.5">
            <Home className="h-3.5 w-3.5" />
            Near your properties
          </h4>
          {loadingMine ? (
            <div className="flex justify-center py-8" data-testid="planning-apps-tool-mine-loading">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : myResults.length === 0 ? (
            <p className="text-xs text-muted-foreground italic" data-testid="planning-apps-tool-mine-empty">
              You don't have any properties with a postcode yet.
            </p>
          ) : (
            <div className="space-y-4" data-testid="planning-apps-tool-mine-results">
              {myResults.map((r) => (
                <div key={r.property_id} className="bg-white border border-border p-4">
                  <p className="text-sm font-medium mb-2">{r.address} <span className="text-muted-foreground font-mono text-xs">({r.postcode})</span></p>
                  {r.applications.length > 0 ? (
                    <div className="space-y-1.5">
                      {r.applications.map((a, i) => <AppRow key={i} a={a} />)}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">No nearby planning applications found.</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
